import React from "react";

import { css } from "@compiled/react";
import { documentToHtmlString } from "@contentful/rich-text-html-renderer";
import {
    Document,
    Node as ContentfulNode,
    BLOCKS,
    INLINES,
    TopLevelBlock
} from "@contentful/rich-text-types";

import { token } from "@atlaskit/tokens";

import {
    Image,
    Panel,
    PullQuote,
    EmbeddedVideo,
    Button
} from "@/libraries/design/components";
import { RichTextComponent } from "@/libraries/design/utils";

export interface UniformBodyContentProps {
    body: Document;
}

interface Node extends ContentfulNode {
    nodeType: BLOCKS | INLINES | "text";
    content?: Node[];
    value?: string;
    marks?: { type: string }[];
    data: { target?: { fields?: object } };
}

interface EmbeddedEntryNode extends Node {
    data: {
        target: {
            fields: object;
            sys: { contentType: { sys: { id: string } } };
        };
    };
}

type embeddedAssetBlockDataType = {
    fields: {
        file: {
            contentType: string;
            details: { image: { width: number; height: number }; size: number };
            fileName: string;
            url: string;
        };
        description: string;
        title: string;
    };
};

type embeddedVideoAssetDataType = {
    embedded?: string;
    embedAssetOverlay?: embeddedAssetBlockDataType;
    embedAssetOverlayOl?: { fields: orangeLogicAssetDataType };
    embeddedVideoAssetNameExternal?: string;
};

type proTipComponentDataType = {
    proTipNameExternal: string;
    proTipRichText?: Document;
    proTipText?: string;
};

type orangeLogicAssetDataType = {
    assetJson?: {
        imageUrl: string;
    };
    assetUrl?: string;
    alt?: string;
};

type callToActionMicrocopyDataType = {
    ctaButtonText: string;
    ctaUrl: string;
};

const UniformBodyContent = ({ body }: UniformBodyContentProps) => {
    const customComponentRenderer = (
        node: EmbeddedEntryNode
    ): React.ReactElement => {
        switch (node.data.target.sys.contentType.sys.id) {
            case "embeddedVideoAsset": {
                const embeddedVideoAssetData = node.data.target
                    .fields as embeddedVideoAssetDataType;
                let embeddedVideoThumbnail = undefined;
                if (embeddedVideoAssetData.embedAssetOverlayOl) {
                    const orangeLogicThumbnail =
                        embeddedVideoAssetData.embedAssetOverlayOl.fields;
                    const imageUrl = orangeLogicThumbnail.assetJson
                        ? orangeLogicThumbnail.assetJson.imageUrl
                        : orangeLogicThumbnail.assetUrl ?? "";

                    embeddedVideoThumbnail = {
                        src: imageUrl,
                        alt: orangeLogicThumbnail.alt ?? ""
                    };
                } else if (embeddedVideoAssetData.embedAssetOverlay) {
                    embeddedVideoThumbnail = {
                        src: embeddedVideoAssetData.embedAssetOverlay.fields
                            .file.url,
                        alt: embeddedVideoAssetData.embedAssetOverlay.fields
                            .description,
                        contentType:
                            embeddedVideoAssetData.embedAssetOverlay.fields.file
                                .contentType
                    };
                }
                if (!embeddedVideoAssetData.embedded) {
                    return <></>;
                }
                return (
                    <div css={css(embeddedItemStyles)}>
                        <EmbeddedVideo
                            embedUrl={embeddedVideoAssetData.embedded}
                            thumbnail={embeddedVideoThumbnail}
                            title={
                                embeddedVideoAssetData.embeddedVideoAssetNameExternal ??
                                ""
                            }
                        />
                    </div>
                );
            }
            case "proTipComponent": {
                const proTipComponentData = node.data.target
                    .fields as proTipComponentDataType;
                return (
                    <div css={css(embeddedItemStyles)}>
                        <Panel
                            panelType="pro-tip"
                            label={proTipComponentData.proTipNameExternal}
                            description={
                                proTipComponentData.proTipRichText ? (
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: documentToHtmlString(
                                                proTipComponentData.proTipRichText
                                            )
                                        }}
                                    />
                                ) : (
                                    proTipComponentData.proTipText ?? ""
                                )
                            }
                        />
                    </div>
                );
            }
            case "orangeLogicAsset": {
                const orangeLogicAssetData = node.data.target
                    .fields as orangeLogicAssetDataType;
                const imageUrl = orangeLogicAssetData.assetJson
                    ? orangeLogicAssetData.assetJson.imageUrl
                    : orangeLogicAssetData.assetUrl ?? undefined;
                if (imageUrl === undefined) {
                    return <></>;
                }
                return (
                    <Image
                        src={imageUrl}
                        alt={orangeLogicAssetData.alt ?? ""}
                        style={embeddedItemStyles}
                    />
                );
            }
            case "callToActionMicrocopy": {
                const callToActionMicrocopyData = node.data.target
                    .fields as callToActionMicrocopyDataType;
                return (
                    <div css={css(embeddedItemStyles)}>
                        <Button
                            href={callToActionMicrocopyData.ctaUrl}
                            dataEventProps={{
                                "data-product-key": "uniform",
                                "data-event-component": "linkButton",
                                "data-event-container": "bodyContentInline",
                                "data-event": "clicked"
                            }}
                        >
                            {callToActionMicrocopyData.ctaButtonText}
                        </Button>
                    </div>
                );
            }
            default: {
                return <></>;
            }
        }
    };

    const renderContentfulRichTextNodes = (nodes: Node[]) => {
        return nodes.map((node) => {
            switch (node.nodeType) {
                case "embedded-entry-block": {
                    return customComponentRenderer(node as EmbeddedEntryNode);
                }
                case "embedded-asset-block": {
                    const embeddedAssetBlockData = node.data
                        .target as embeddedAssetBlockDataType;
                    return (
                        <Image
                            src={embeddedAssetBlockData.fields.file.url}
                            alt={embeddedAssetBlockData.fields.description}
                            style={embeddedItemStyles}
                        />
                    );
                }
                case "blockquote": {
                    return (
                        <PullQuote
                            quote=""
                            quoteRichTextString={parseRichTextStringForOnlyContentInsideParagraphs(
                                documentToHtmlString({
                                    nodeType: BLOCKS.DOCUMENT,
                                    content: node.content as TopLevelBlock[],
                                    data: {}
                                })
                            )}
                        />
                    );
                }
                default: {
                    return (
                        <RichTextComponent
                            text={documentToHtmlString({
                                nodeType: BLOCKS.DOCUMENT,
                                content: [node as TopLevelBlock],
                                data: {}
                            })}
                        />
                    );
                }
            }
        });
    };

    return (
        <div css={bodyContainerCss}>
            {renderContentfulRichTextNodes(body.content)}
        </div>
    );
};

const embeddedItemStyles = {
    maxWidth: "100%",
    marginBlock: token("space.200")
};

const bodyContainerCss = css({
    display: "flex",
    flexDirection: "column",
    columnGap: token("space.100"),
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["div:has(p:last-child) + div:has(p:first-child)"]: {
        // will only apply to divs with paragraphs that immediately follow another div with paragraph
        marginBlockStart: token("space.200")
    }
});

const parseRichTextStringForOnlyContentInsideParagraphs = (
    richTextString: string
): string => {
    const paragraphRegex = /<p\b[^>]*>(.*?)<\/p>/gi;
    const matches = Array.from(richTextString.matchAll(paragraphRegex));
    return matches ? matches.map((m) => m[1]).join() : richTextString;
};

export default UniformBodyContent;
